// Sidebar's lazy-loaded dialog block — extracted from Sidebar.tsx during the
// P5 pass-4 split. Pure render: every input is a prop, no state, no effects.
// Owns the two <Suspense> boundaries that wrap the workspace-scope and
// home-scope dialogs.

import { lazy, Suspense } from "react";

const CreateWorkspaceDialog = lazy(() =>
  import("@/components/dialogs/CreateWorkspaceDialog").then((m) => ({
    default: m.CreateWorkspaceDialog,
  })),
);
const CreateChannelDialog = lazy(() =>
  import("@/components/dialogs/CreateChannelDialog").then((m) => ({
    default: m.CreateChannelDialog,
  })),
);
const InviteDialog = lazy(() =>
  import("@/components/dialogs/InviteDialog").then((m) => ({
    default: m.InviteDialog,
  })),
);
const EditChannelDialog = lazy(() =>
  import("@/components/dialogs/EditChannelDialog").then((m) => ({
    default: m.EditChannelDialog,
  })),
);
const DeleteConfirmDialog = lazy(() =>
  import("@/components/dialogs/DeleteConfirmDialog").then((m) => ({
    default: m.DeleteConfirmDialog,
  })),
);
const NewDMDialog = lazy(() =>
  import("@/components/dialogs/NewDMDialog").then((m) => ({
    default: m.NewDMDialog,
  })),
);
const BrowseChannelsDialog = lazy(() =>
  import("@/components/dialogs/BrowseChannelsDialog").then((m) => ({
    default: m.BrowseChannelsDialog,
  })),
);
const WorkspaceSettingsDialog = lazy(() =>
  import("@/components/dialogs/WorkspaceSettingsDialog").then((m) => ({
    default: m.WorkspaceSettingsDialog,
  })),
);
const CreateClientSpaceDialog = lazy(() =>
  import("@/components/dialogs/CreateClientSpaceDialog").then((m) => ({
    default: m.CreateClientSpaceDialog,
  })),
);
const ClientSpaceSettingsDialog = lazy(() =>
  import("@/components/dialogs/ClientSpaceSettingsDialog").then((m) => ({
    default: m.ClientSpaceSettingsDialog,
  })),
);

type CreateChannelContext =
  | { spaceId: string; spaceName: string; hostWorkspaceId?: string }
  | "workspace"
  | null;

interface SidebarDialogsProps {
  // User + workspace context
  user: { id: string } | null | undefined;
  selectedWorkspace: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  currentWorkspace: any;
  isWorkspaceAdmin: boolean;
  isHomeScope: boolean;

  // Workspace-scope users / DMs context
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  activeUserList: any[];
  wsMemberIdSet: Set<string>;
  collaboratorIds: Set<string>;
  existingDmUserIds: Set<string>;

  // Suspense fallback signal
  hasPendingSidebarDialog: boolean;

  // CreateWorkspaceDialog (workspace scope)
  showCreateWorkspace: boolean;
  setShowCreateWorkspace: (open: boolean) => void;
  onSelectWorkspace: (id: string | null) => void;
  onSelectChannel: (id: string | null, name?: string) => void;

  // CreateChannelDialog
  createChannelContext: CreateChannelContext;
  setCreateChannelContext: (ctx: CreateChannelContext) => void;
  setExpandedSpaces: (
    updater: (prev: Record<string, boolean>) => Record<string, boolean>,
  ) => void;

  // NewDMDialog (workspace + home)
  showNewDM: boolean;
  setShowNewDM: (open: boolean) => void;

  // BrowseChannelsDialog
  showBrowseChannels: boolean;
  setShowBrowseChannels: (open: boolean) => void;
  setSelectedClientSpace: (id: string | null) => void;

  // WorkspaceSettingsDialog
  showWorkspaceSettings: boolean;
  setShowWorkspaceSettings: (open: boolean) => void;
  handleLeaveWorkspace: () => void;

  // CreateClientSpaceDialog
  showCreateClientSpace: boolean;
  setShowCreateClientSpace: (open: boolean) => void;

  // ClientSpaceSettingsDialog
  selectedClientSpace: string | null;

  // InviteDialog
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  inviteContext: any;
  setInviteContext: (ctx: null) => void;

  // EditChannelDialog + DeleteConfirmDialog
  showEditChannel: boolean;
  setShowEditChannel: (open: boolean) => void;
  showDeleteChannel: boolean;
  setShowDeleteChannel: (open: boolean) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  contextChannel: any;
  handleDeleteChannel: () => void;

  // Home-scope dialog context
  showCreateWorkspaceHome: boolean;
  setShowCreateWorkspaceHome: (open: boolean) => void;
  bestLookupWorkspaceId: string | undefined;
  openChannelWithContextProp:
    | ((
        channelId: string,
        opts?: { workspaceId?: string | null; targetMessageId?: string },
      ) => void)
    | undefined;
  onScopeChange: (scope: string) => void;
  // Marks a just-created workspace as "pending" so the auto-recovery
  // effect in use-workspace-scope doesn't bounce scope to "home" while
  // the CP-list refetch is in flight.
  setPendingWorkspace?: (id: string | null) => void;
}

export function SidebarDialogs({
  user,
  selectedWorkspace,
  currentWorkspace,
  isWorkspaceAdmin,
  isHomeScope,
  activeUserList,
  wsMemberIdSet,
  collaboratorIds,
  existingDmUserIds,
  hasPendingSidebarDialog,
  showCreateWorkspace,
  setShowCreateWorkspace,
  onSelectWorkspace,
  onSelectChannel,
  createChannelContext,
  setCreateChannelContext,
  setExpandedSpaces,
  showNewDM,
  setShowNewDM,
  showBrowseChannels,
  setShowBrowseChannels,
  setSelectedClientSpace,
  showWorkspaceSettings,
  setShowWorkspaceSettings,
  handleLeaveWorkspace,
  showCreateClientSpace,
  setShowCreateClientSpace,
  selectedClientSpace,
  inviteContext,
  setInviteContext,
  showEditChannel,
  setShowEditChannel,
  showDeleteChannel,
  setShowDeleteChannel,
  contextChannel,
  handleDeleteChannel,
  showCreateWorkspaceHome,
  setShowCreateWorkspaceHome,
  bestLookupWorkspaceId,
  openChannelWithContextProp,
  onScopeChange,
  setPendingWorkspace,
}: SidebarDialogsProps) {
  return (
    <>
      {/* Workspace-specific dialogs */}
      <Suspense
        fallback={
          hasPendingSidebarDialog ? (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-overlay/25"
              aria-hidden="true"
            >
              <div className="rounded border border-border bg-background px-3 py-2 text-xs text-muted-foreground">
                Loading dialog...
              </div>
            </div>
          ) : null
        }
      >
        {user && (
          <>
            <CreateWorkspaceDialog
              open={showCreateWorkspace}
              onOpenChange={setShowCreateWorkspace}
              userId={user.id}
              onCreated={(wsId) => {
                // Mark pending BEFORE setting scope so the auto-recovery
                // effect doesn't bounce to "home" while the CP-list
                // refetch (triggered inside the dialog) is in flight.
                setPendingWorkspace?.(wsId);
                onSelectWorkspace(wsId);
                onSelectChannel(null);
              }}
            />
            {selectedWorkspace && (
              <>
                <CreateChannelDialog
                  open={!!createChannelContext}
                  onOpenChange={(open) => {
                    if (!open) setCreateChannelContext(null);
                  }}
                  workspaceId={
                    typeof createChannelContext === "object" &&
                    createChannelContext?.hostWorkspaceId
                      ? createChannelContext.hostWorkspaceId
                      : selectedWorkspace
                  }
                  userId={user.id}
                  spaceId={
                    typeof createChannelContext === "object"
                      ? createChannelContext?.spaceId
                      : undefined
                  }
                  spaceName={
                    typeof createChannelContext === "object"
                      ? createChannelContext?.spaceName
                      : undefined
                  }
                  onCreatedInSpace={(spaceId) => {
                    setExpandedSpaces((prev) => ({
                      ...prev,
                      [spaceId]: true,
                    }));
                  }}
                />
                <NewDMDialog
                  open={showNewDM}
                  onOpenChange={setShowNewDM}
                  currentUserId={user.id}
                  allUsers={activeUserList}
                  workspaceName={currentWorkspace?.name || "Workspace"}
                  wsMemberIds={wsMemberIdSet}
                  lookupWorkspaceId={selectedWorkspace}
                  collaboratorIds={collaboratorIds}
                  existingDmUserIds={existingDmUserIds}
                  onCreated={onSelectChannel}
                />
                <BrowseChannelsDialog
                  open={showBrowseChannels}
                  onOpenChange={setShowBrowseChannels}
                  workspaceId={selectedWorkspace}
                  currentUserId={user.id}
                  onSelectChannel={onSelectChannel}
                  onCreateChannel={() => setCreateChannelContext("workspace")}
                  onOpenSpaceSettings={setSelectedClientSpace}
                />
                <WorkspaceSettingsDialog
                  open={showWorkspaceSettings}
                  onOpenChange={setShowWorkspaceSettings}
                  workspaceId={selectedWorkspace}
                  onLeaveWorkspace={handleLeaveWorkspace}
                />
                <CreateClientSpaceDialog
                  open={showCreateClientSpace}
                  onOpenChange={setShowCreateClientSpace}
                  workspaceId={selectedWorkspace}
                />
                {selectedClientSpace && (
                  <ClientSpaceSettingsDialog
                    open={!!selectedClientSpace}
                    onOpenChange={(open) => {
                      if (!open) setSelectedClientSpace(null);
                    }}
                    spaceId={selectedClientSpace}
                    currentUserId={user.id}
                    isWorkspaceAdmin={isWorkspaceAdmin}
                  />
                )}
              </>
            )}
          </>
        )}
        {inviteContext && (
          <InviteDialog
            open={!!inviteContext}
            onOpenChange={(open) => {
              if (!open) setInviteContext(null);
            }}
            context={inviteContext}
          />
        )}
        <EditChannelDialog
          open={showEditChannel}
          onOpenChange={setShowEditChannel}
          channel={contextChannel}
        />
        <DeleteConfirmDialog
          open={showDeleteChannel}
          onOpenChange={setShowDeleteChannel}
          onConfirm={handleDeleteChannel}
          title="Delete channel"
          description={`Are you sure you want to delete #${contextChannel?.name}? All messages will be lost.`}
        />
      </Suspense>

      {/* Home scope dialogs */}
      <Suspense>
        {isHomeScope && user && showNewDM && (
          <NewDMDialog
            open={showNewDM}
            onOpenChange={setShowNewDM}
            currentUserId={user.id}
            allUsers={activeUserList}
            lookupWorkspaceId={bestLookupWorkspaceId}
            existingDmUserIds={existingDmUserIds}
            onCreated={(channelId) =>
              openChannelWithContextProp
                ? openChannelWithContextProp(channelId, {
                    workspaceId: null,
                  })
                : onSelectChannel(channelId)
            }
          />
        )}
        {isHomeScope && user && showCreateWorkspaceHome && (
          <CreateWorkspaceDialog
            open={showCreateWorkspaceHome}
            onOpenChange={setShowCreateWorkspaceHome}
            userId={user.id}
            onCreated={(wsId) => {
              // Mark pending so auto-recovery doesn't bounce while the
              // CP-list refetch is in flight.
              setPendingWorkspace?.(wsId);
              onScopeChange(wsId);
            }}
          />
        )}
      </Suspense>
    </>
  );
}
